import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Radio } from "lucide-react";
import { EASE } from "@/lib/site";

export default function HeroFrame() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <div data-testid="hero-visual" onMouseMove={onMove} onMouseLeave={onLeave} className="relative mx-auto max-w-md lg:max-w-none" style={{ perspective: 1200 }}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative">
        <div className="relative aspect-[4/5]" style={{ transformStyle: "preserve-3d" }}>
          <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.3, ease: EASE }}
            className="absolute inset-x-0 bottom-0 top-[16%]">
            <div className="chamfer grain absolute inset-0 bg-[var(--navy)] overflow-hidden">
            <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(255,255,255,0.22),transparent_60%)]" />
            <div aria-hidden className="absolute -left-6 top-4 font-display italic font-black text-[11rem] sm:text-[13rem] leading-none text-transparent [-webkit-text-stroke:2px_rgba(255,255,255,0.18)] select-none">S4S</div>
            <div aria-hidden className="absolute -left-10 bottom-[30%] w-[130%] h-[6px] bg-[var(--red)] -rotate-[16deg]" />
            <div aria-hidden className="absolute -left-10 bottom-[24%] w-[130%] h-[2px] bg-[#D91219]/70 -rotate-[16deg]" />
            </div>
          </motion.div>
          <div aria-hidden className="absolute left-[12%] right-[12%] bottom-[2%] h-6 rounded-[50%] bg-black/45 blur-xl" style={{ transform: "translateZ(20px)" }} />
          <motion.img data-testid="hero-guards-image" src="/images/hero-guards.webp?v=2" alt="S4s Enterprises security team in uniform"
            initial={{ opacity: 0, y: 80 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.3, delay: 0.7, ease: EASE }}
            style={{ x: "-50%", z: 50 }}
            className="absolute bottom-0 left-1/2 h-full w-auto max-w-none object-contain drop-shadow-[0_25px_35px_rgba(11,15,38,0.45)]" />
        </div>
        <motion.div style={{ z: 90 }} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
          data-testid="hero-badge" className="absolute -right-3 sm:-right-10 bottom-[16%] bg-white chamfer-sm shadow-[0_30px_60px_-20px_rgba(11,15,38,0.45)] px-4 py-3 sm:px-5 sm:py-4">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--red)]">
            <span className="relative flex w-2 h-2"><span className="absolute inset-0 rounded-full bg-[var(--red)] animate-ping" /><span className="relative w-2 h-2 rounded-full bg-[var(--red)]" /></span>
            Live
          </div>
          <div className="font-display italic font-black text-4xl sm:text-5xl leading-none text-[var(--navy)] mt-1">24/7</div>
          <div className="text-xs text-slate-600 flex items-center gap-1 mt-1"><Radio className="w-3.5 h-3.5" /> QRT Support</div>
        </motion.div>
        <div aria-hidden style={{ transform: "translateZ(30px)" }} className="absolute -right-3 -bottom-3 w-28 h-28 border-r-[6px] border-b-[6px] border-[var(--red)]" />
      </motion.div>
    </div>
  );
}
